<template>
  <div class="cmd-palette-mask" @click.self="close">
    <div class="cmd-palette" @click.self="close">
      <!-- 搜索输入 -->
      <div class="cmd-input-wrap">
        <i class="ri-search-line"></i>
        <input
          ref="input"
          v-model="query"
          class="cmd-input"
 type="text"
          placeholder="搜索笔记、精选或跳转页面..."
          @input="onInput"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="selectActive"
        />
        <span class="cmd-kbd">ESC</span>
      </div>

      <!-- 结果区 -->
      <div ref="results" class="cmd-results">
        <!-- 快捷导航（无搜索词时显示） -->
        <template v-if="!query">
          <div class="cmd-group-title">快捷导航</div>
          <div
            v-for="(item, idx) in navItems"
            :key="'nav-' + idx"
            class="cmd-item"
            :class="{ active: activeIdx === idx }"
            @mouseenter="activeIdx = idx"
            @click="runNav(item)"
          >
            <i :class="item.icon"></i>
            <div class="cmd-item-text">
              <div class="cmd-item-title">{{ item.title }}</div>
              <div class="cmd-item-sub">{{ item.subtitle }}</div>
            </div>
            <i class="ri-arrow-right-s-line cmd-item-arrow"></i>
          </div>
        </template>

        <!-- 搜索结果 -->
        <template v-else>
          <!-- 搜索中 -->
          <div v-if="searching" class="cmd-empty">搜索中…</div>
          <template v-else>
            <!-- 笔记结果 -->
            <div v-if="noteResults.length" class="cmd-group-title">学习笔记</div>
            <div
              v-for="(item, idx) in noteResults"
              :key="'note-' + item.push_date"
              class="cmd-item"
              :class="{ active: activeIdx === navItems.length + idx }"
              @mouseenter="activeIdx = navItems.length + idx"
              @click="goNote(item.push_date)"
            >
              <i class="ri-book-2-line"></i>
              <div class="cmd-item-text">
                <div class="cmd-item-title">{{ item.title }}</div>
                <div class="cmd-item-sub">{{ item.push_date }}</div>
              </div>
              <i class="ri-arrow-right-s-line cmd-item-arrow"></i>
            </div>

            <!-- 精选结果 -->
            <div v-if="selectedResults.length" class="cmd-group-title">每日精选</div>
            <div
              v-for="(item, idx) in selectedResults"
              :key="'sel-' + idx"
              class="cmd-item"
              :class="{ active: activeIdx === navItems.length + noteResults.length + idx }"
              @mouseenter="activeIdx = navItems.length + noteResults.length + idx"
              @click="goSelected"
            >
              <i class="ri-radar-line"></i>
              <div class="cmd-item-text">
                <div class="cmd-item-title">{{ item.title }}</div>
                <div class="cmd-item-sub">{{ item.source }}</div>
              </div>
              <i class="ri-arrow-right-s-line cmd-item-arrow"></i>
            </div>

            <!-- 无结果 -->
            <div v-if="!noteResults.length && !selectedResults.length" class="cmd-empty">
              <i class="ri-search-line"></i> 没有找到 "{{ query }}" 的相关内容
            </div>
          </template>
        </template>
      </div>

      <!-- 底部快捷键提示 -->
      <div class="cmd-footer">
        <span><kbd>↑</kbd> <kbd>↓</kbd> 导航</span>
        <span><kbd>Enter</kbd> 确认</span>
        <span><kbd>Esc</kbd> 关闭</span>
      </div>
    </div>
  </div>
</template>

<script>
import aiPush from '../api/aiPush'
import aiPushSelected from '../api/aiPushSelected'

export default {
  name: 'CommandPalette',
  data () {
    return {
      query: '',
      activeIdx: 0,
      searching: false,
      noteResults: [],
      selectedResults: [],
      searchTimer: null,
      navItems: [
        { title: '每日精选', subtitle: '查看今日 AI 精选', icon: 'ri-radar-line', hash: '#/' },
        { title: '学习笔记', subtitle: '浏览全部学习笔记', icon: 'ri-book-2-line', hash: '#/notes' },
        { title: '学习统计', subtitle: '查看学习数据统计', icon: 'ri-bar-chart-2-line', hash: '#/stats' },
        { title: '年度总结', subtitle: '回顾本年度学习', icon: 'ri-gift-line', hash: '#/year' },
        { title: '关于我', subtitle: '了解站点信息', icon: 'ri-user-line', hash: '#/about' }
      ]
    }
  },
  computed: {
    totalItems () {
      return this.navItems.length + this.noteResults.length + this.selectedResults.length
    }
  },
  watch: {
    totalItems () {
      this.activeIdx = 0
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.input) this.$refs.input.focus()
    })
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount () {
    document.removeEventListener('keydown', this.onKeydown)
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    close () {
      this.$emit('close')
    },
    onKeydown (e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
      }
    },
    onInput () {
      this.activeIdx = 0
      if (this.searchTimer) clearTimeout(this.searchTimer)
      if (!this.query.trim()) {
        this.noteResults = []
        this.selectedResults = []
        this.searching = false
        return
      }
      this.searching = true
      this.searchTimer = setTimeout(this.doSearch, 250)
    },
    async doSearch () {
      const kw = this.query.trim()
      try {
        const [noteRes, selRes] = await Promise.all([
          aiPush.list({ keyword: kw, page: 1, size: 5 }),
          aiPushSelected.list({ page: 1, size: 5 })
        ])
        // 笔记结果按关键词过滤标题
        this.noteResults = (noteRes.data || []).filter(n =>
          (n.title || '').toLowerCase().includes(kw.toLowerCase())
        )
        // 精选结果按关键词过滤标题
        this.selectedResults = (selRes.data || []).filter(s =>
          (s.title || '').toLowerCase().includes(kw.toLowerCase())
        )
      } catch (e) {
        // 静默失败，不打扰用户
        this.noteResults = []
        this.selectedResults = []
      }
      this.searching = false
    },
    moveActive (dir) {
      const total = this.totalItems
      if (total === 0) return
      this.activeIdx = (this.activeIdx + dir + total) % total
      this.scrollActiveIntoView()
    },
    scrollActiveIntoView () {
      this.$nextTick(() => {
        const el = this.$refs.results.querySelector('.cmd-item.active')
        if (el) el.scrollIntoView({ block: 'nearest' })
      })
    },
    selectActive () {
      const total = this.totalItems
      if (total === 0) return
      const idx = this.activeIdx
      if (idx < this.navItems.length) {
        this.runNav(this.navItems[idx])
      } else if (idx < this.navItems.length + this.noteResults.length) {
        const note = this.noteResults[idx - this.navItems.length]
        this.goNote(note.push_date)
      } else {
        this.goSelected()
      }
    },
    runNav (item) {
      window.location.hash = item.hash
      this.close()
    },
    goNote (date) {
      window.location.hash = `#/date/${date}`
      this.close()
    },
    goSelected () {
      window.location.hash = '#/'
      this.close()
    }
  }
}
</script>
